import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { api, buildLeadQuery, type LeadQuery } from './client';
import type {
  BulkAssignResult,
  CrmUser,
  CustomField,
  DistributeResult,
  Lead,
  LeadListResponse,
  LeadCampaign,
  Campaign,
  ReportSend,
  LoginActivityPage,
  LeadSourceOption,
  LeadStatusDef,
  LeadSubStatus,
  ManagerRoutingState,
  PermissionCatalogue,
  Role,
  RolePermissions,
  PerformanceData,
  RangePreset,
  Team,
  TeamMember,
  TimelineEntry,
  UserPermissions,
  ApiKey,
  IssuedApiKey,
  MetaAccount,
  MetaAdAccount,
  MetaAdAccountsResponse,
  MetaCampaign,
  MetaConfig,
  MetaFieldMapping,
  MetaLeadForm,
  MetaPagesResponse,
  MetaStatus,
  CallConfig,
  CallLog,
  AgentDevice,
  PairingCode,
  LeadCallHistory,
  WhatsAppSettingsInput,
  WhatsAppStatus,
  WhatsAppTemplate,
  WhatsAppVariable,
} from './types';

/* --------------------------------- calls --------------------------------- */

/**
 * Whether to offer a Call button at all. Cached hard: it changes only when an
 * admin edits this user's device, and every lead row would otherwise ask.
 */
export function useCallConfig() {
  return useQuery({
    queryKey: ['calls', 'config'],
    queryFn: () => api<CallConfig>('/calls/config'),
    staleTime: 5 * 60_000,
  });
}

/**
 * A lead's call history plus its aggregates, newest first.
 *
 * Returns the whole envelope rather than just the rows: the summary is computed
 * server-side over every call on the lead, which is not the same as reducing
 * the returned page — and the list is capped.
 */
export function useLeadCalls(leadId: string | undefined) {
  return useQuery({
    queryKey: ['calls', 'lead', leadId],
    queryFn: () => api<LeadCallHistory>(`/leads/${leadId}/calls`),
    enabled: Boolean(leadId),
  });
}

/**
 * Places a call.
 *
 * Resolves when Asterisk *accepts* the origination, which is not the same as
 * the call connecting — the outcome arrives over the event stream. The lead's
 * history and timeline are invalidated so the new row appears immediately.
 */
export function useInitiateCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api<CallLog>('/calls/initiate', { method: 'POST', body: { lead_id: leadId } }),
    onSuccess: (call) => {
      void qc.invalidateQueries({ queryKey: ['calls', 'lead', call.lead_id] });
      void qc.invalidateQueries({ queryKey: ['lead', call.lead_id] });
    },
  });
}

/* -------------------------------- devices -------------------------------- */

export function useDevices(all = false) {
  return useQuery({
    queryKey: ['devices', all],
    queryFn: () => api<{ data: AgentDevice[] }>(`/devices${all ? '?all=true' : ''}`).then((r) => r.data),
  });
}

/**
 * Issues a pairing code. Not a query: each call mints a new single-use code,
 * so caching one would hand the same dead code out twice.
 */
export function useCreatePairingCode() {
  return useMutation({
    mutationFn: (userId?: string) =>
      api<PairingCode>(userId ? `/devices/pairing-code/${userId}` : '/devices/pairing-code', {
        method: 'POST',
      }),
  });
}

export function useRevokeDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/devices/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices'] });
      // The Call button depends on whether a phone is paired.
      void qc.invalidateQueries({ queryKey: ['calls', 'config'] });
    },
  });
}

/* ----------------------------- custom fields ----------------------------- */

/**
 * The field registry. Cached aggressively: it changes only when an admin edits
 * it, but almost every screen needs it — the lead table needs it to label
 * columns, forms need it to render inputs, filters need it to know what is
 * filterable.
 */
export function useCustomFields(includeInactive = false) {
  return useQuery({
    queryKey: ['custom-fields', includeInactive],
    queryFn: () =>
      api<{ data: CustomField[] }>(
        `/custom-fields${includeInactive ? '?include_inactive=true' : ''}`,
      ).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useCreateCustomField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<CustomField>('/custom-fields', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields'] }),
  });
}

export function useUpdateCustomField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<CustomField>(`/custom-fields/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields'] }),
  });
}

export function useDeleteCustomField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/custom-fields/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields'] }),
  });
}

export function useAddOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, label }: { fieldId: string; label: string }) =>
      api<CustomField>(`/custom-fields/${fieldId}/options`, { method: 'POST', body: { label } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields'] }),
  });
}

export function useUpdateOption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      optionId,
      ...body
    }: { fieldId: string; optionId: string } & Record<string, unknown>) =>
      api<CustomField>(`/custom-fields/${fieldId}/options/${optionId}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields'] }),
  });
}

/* --------------------------------- leads --------------------------------- */

export function useLeads(query: LeadQuery, enabled = true) {
  return useQuery({
    queryKey: ['leads', query],
    queryFn: () => api<LeadListResponse>(`/leads${buildLeadQuery(query)}`),
    // Keeps the previous page on screen while the next loads, so the table
    // doesn't collapse to a spinner on every filter keystroke.
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * Ids of every lead matching a filter, for "select all N". A mutation rather
 * than a query because it runs on a click, not on render.
 */
export function useLeadIds() {
  return useMutation({
    mutationFn: (query: LeadQuery) =>
      api<{ data: string[]; truncated: boolean }>(`/leads/ids${buildLeadQuery(query)}`),
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => api<Lead>(`/leads/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * The sub-status vocabulary for the whole org, filtered client-side by the
 * lead's status. It is a few dozen short rows that change about never, so one
 * cached fetch beats a request per lead opened.
 */
/** Campaign names present on the caller's leads. Drives the Leads filter. */
export function useLeadSources() {
  return useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => api<{ data: LeadSourceOption[] }>('/leads/sources').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useLeadCampaigns() {
  return useQuery({
    queryKey: ['lead-campaigns'],
    queryFn: () => api<{ data: LeadCampaign[] }>('/leads/campaigns').then((r) => r.data),
    staleTime: 10 * 60_000,
  });
}

export function useLeadSubStatuses() {
  return useQuery({
    queryKey: ['lead-sub-statuses'],
    queryFn: () => api<{ data: LeadSubStatus[] }>('/leads/sub-statuses').then((r) => r.data),
    staleTime: 10 * 60_000,
  });
}

export function useSetLeadSubStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sub_status }: { id: string; sub_status: string | null }) =>
      api<Lead>(`/leads/${id}/sub-status`, { method: 'POST', body: { sub_status } }),
    onSuccess: (lead) => {
      qc.setQueryData(['lead', lead.id], lead);
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/** A counsellor's remark. Lands on the timeline, so both queries go stale. */
export function useAddLeadNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api<{ ok: true }>(`/leads/${id}/notes`, { method: 'POST', body: { body } }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['lead-timeline', vars.id] });
      qc.invalidateQueries({ queryKey: ['lead', vars.id] });
    },
  });
}

export function useLeadTimeline(id: string | undefined) {
  return useQuery({
    queryKey: ['lead-timeline', id],
    queryFn: () => api<{ data: TimelineEntry[] }>(`/leads/${id}/timeline`).then((r) => r.data),
    enabled: Boolean(id),
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Lead>('/leads', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<Lead>(`/leads/${id}`, { method: 'PATCH', body }),
    onSuccess: (lead) => {
      qc.setQueryData(['lead', lead.id], lead);
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useChangeStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      api<Lead>(`/leads/${id}/status`, { method: 'POST', body: { status, reason } }),
    onSuccess: (lead) => {
      qc.setQueryData(['lead', lead.id], lead);
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead-timeline', lead.id] });
    },
  });
}

export function useAssignLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      user_id,
      reason,
    }: {
      id: string;
      user_id: string | null;
      reason?: string;
    }) => api<Lead>(`/leads/${id}/assign`, { method: 'POST', body: { user_id, reason } }),
    onSuccess: (lead) => {
      qc.setQueryData(['lead', lead.id], lead);
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead-timeline', lead.id] });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/leads/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Server caps for a single call; a "select all" selection can exceed both. */
const BULK_ASSIGN_CHUNK = 200;
const DISTRIBUTE_CHUNK = 500;

export function useBulkAssignLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { lead_ids: string[]; user_id: string | null; reason?: string }) => {
      const merged: BulkAssignResult = { assigned: [], skipped: [] };
      for (let i = 0; i < body.lead_ids.length; i += BULK_ASSIGN_CHUNK) {
        const res = await api<BulkAssignResult>('/leads/bulk-assign', {
          method: 'POST',
          body: { ...body, lead_ids: body.lead_ids.slice(i, i + BULK_ASSIGN_CHUNK) },
        });
        merged.assigned.push(...res.assigned);
        merged.skipped.push(...res.skipped);
      }
      return merged;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

export function useDistributeLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { lead_ids?: string[]; team_ids?: string[] }) => {
      if (!body.lead_ids) return api<DistributeResult>('/leads/distribute', { method: 'POST', body });

      // Each chunk starts its own round robin, so the split across members can
      // be off by one per chunk. Merging keeps the summary banner honest.
      let merged: DistributeResult | null = null;
      for (let i = 0; i < body.lead_ids.length; i += DISTRIBUTE_CHUNK) {
        const res = await api<DistributeResult>('/leads/distribute', {
          method: 'POST',
          body: { ...body, lead_ids: body.lead_ids.slice(i, i + DISTRIBUTE_CHUNK) },
        });
        if (!merged) {
          merged = { ...res, by_member: res.by_member.map((m) => ({ ...m })) };
          continue;
        }
        merged.assigned += res.assigned;
        merged.skipped.push(...res.skipped);
        for (const member of res.by_member) {
          const seen = merged.by_member.find((m) => m.user_id === member.user_id);
          if (seen) seen.count += member.count;
          else merged.by_member.push({ ...member });
        }
      }
      return merged!;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/* ---------------------------- manager routing ---------------------------- */

export function useManagerRouting() {
  return useQuery({
    queryKey: ['manager-routing'],
    queryFn: () => api<ManagerRoutingState>('/assignment/manager-routing'),
  });
}

export function useSetManagerRouting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api<ManagerRoutingState>('/assignment/manager-routing', { method: 'POST', body: { enabled } }),
    onSuccess: (data) => qc.setQueryData(['manager-routing'], data),
  });
}

/* ----------------------------- performance ----------------------------- */

export interface PerformanceQuery {
  range: RangePreset;
  from?: string;
  to?: string;
  teamId?: string;
}

export function usePerformance(query: PerformanceQuery) {
  const params = new URLSearchParams({ range: query.range });
  if (query.range === 'custom' && query.from && query.to) {
    params.set('from', query.from);
    params.set('to', query.to);
  }
  if (query.teamId) params.set('team_id', query.teamId);

  return useQuery({
    queryKey: ['performance', params.toString()],
    queryFn: () => api<PerformanceData>(`/performance?${params}`),
    // A custom range is only valid once both ends are picked; firing on the
    // half-filled state would 400 and flash an error while the user is still
    // choosing the second date.
    enabled: query.range !== 'custom' || Boolean(query.from && query.to),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

/* ----------------------------- users & teams ----------------------------- */

/**
 * The staff directory.
 *
 * `enabled` matters: listing users needs user.manage / lead.assign / team.manage,
 * so a counsellor calling this gets a 403. Callers must gate it on the
 * permission rather than letting every lead page fire a failing request.
 */
export function useUsers(includeInactive = true, enabled = true) {
  return useQuery({
    queryKey: ['users', includeInactive],
    queryFn: () =>
      api<{ data: CrmUser[] }>(`/users?include_inactive=${includeInactive}`).then((r) => r.data),
    staleTime: 60_000,
    enabled,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api<{ data: Role[] }>('/roles').then((r) => r.data),
    staleTime: 10 * 60_000,
  });
}

export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ data: Team[] }>('/teams').then((r) => r.data),
    staleTime: 10 * 60_000,
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<Team>(`/teams/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Team>('/teams', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  });
}

export function useTeamMembers(teamId: string | null) {
  return useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => api<{ data: TeamMember[] }>(`/teams/${teamId}/members`).then((r) => r.data),
    enabled: Boolean(teamId),
    staleTime: 30_000,
  });
}

/**
 * Who this team can still take on: active users not already under another
 * manager. Only fetched while a picker is open — the list goes stale the
 * moment any manager claims someone.
 */
export function useTeamCandidates(teamId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['team-candidates', teamId],
    queryFn: () => api<{ data: CrmUser[] }>(`/teams/${teamId}/candidates`).then((r) => r.data),
    enabled: Boolean(teamId) && enabled,
    staleTime: 0,
  });
}

/**
 * Both roster mutations return the new roster, so it is written straight into
 * the cache rather than refetched. Teams and users are invalidated too: member
 * counts and the "Teams" column on the user table both just changed.
 */
function useRosterMutation(
  fn: (teamId: string, userId: string) => Promise<{ data: TeamMember[] }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) => fn(teamId, userId),
    onSuccess: (res, { teamId }) => {
      qc.setQueryData(['team-members', teamId], res.data);
      qc.invalidateQueries({ queryKey: ['team-candidates'] });
      qc.invalidateQueries({ queryKey: ['teams'] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useAddTeamMember() {
  return useRosterMutation((teamId, userId) =>
    api<{ data: TeamMember[] }>(`/teams/${teamId}/members`, {
      method: 'POST',
      body: { user_id: userId },
    }),
  );
}

export function useRemoveTeamMember() {
  return useRosterMutation((teamId, userId) =>
    api<{ data: TeamMember[] }>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
  );
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api<CrmUser>('/users', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<CrmUser>(`/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api<void>(`/users/${id}/password`, { method: 'POST', body: { password } }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

/* ------------------------------ permissions ------------------------------ */

export function usePermissionCatalogue() {
  return useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: () => api<PermissionCatalogue>('/permissions'),
    staleTime: 30 * 60_000, // developer-controlled; changes only on deploy
  });
}

export function useRolePermissions(roleId: string | undefined) {
  return useQuery({
    queryKey: ['role-permissions', roleId],
    queryFn: () => api<RolePermissions>(`/roles/${roleId}/permissions`),
    enabled: Boolean(roleId),
  });
}

export function useSetRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: string; permissions: string[] }) =>
      api<{ permissions: string[] }>(`/roles/${roleId}/permissions`, {
        method: 'PUT',
        body: { permissions },
      }),
    onSuccess: (_data, { roleId }) => {
      void qc.invalidateQueries({ queryKey: ['role-permissions', roleId] });
      void qc.invalidateQueries({ queryKey: ['roles'] });
      // A role change alters what its holders can do.
      void qc.invalidateQueries({ queryKey: ['user-permissions'] });
    },
  });
}

export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: () => api<UserPermissions>(`/users/${userId}/permissions`),
    enabled: Boolean(userId),
  });
}

export function useSetUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, permissions }: { userId: string; permissions: Record<string, boolean> }) =>
      api<UserPermissions>(`/users/${userId}/permissions`, { method: 'PUT', body: { permissions } }),
    onSuccess: (data) => {
      qc.setQueryData(['user-permissions', data.user.id], data);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/* ------------------------- Meta Lead Ads integration ------------------------ */

/**
 * The whole settings screen hangs off this one call.
 *
 * Never polled: every mutation in this module invalidates the `['meta']` key,
 * which this query lives under, so the six setup steps re-read themselves after
 * anything that could have moved them. The one case invalidation misses — a
 * mutation that *failed* after changing server state — is handled by the page,
 * which invalidates explicitly on error.
 */
export function useMetaStatus() {
  return useQuery({
    queryKey: ['meta', 'status'],
    queryFn: () => api<MetaStatus>('/integrations/meta/status'),
  });
}

export function useMetaConfig() {
  return useQuery({
    queryKey: ['meta', 'config'],
    queryFn: () => api<{ data: MetaConfig }>('/integrations/meta/config').then((r) => r.data),
  });
}

export function useSaveMetaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { app_id: string; app_secret?: string; verify_token?: string }) =>
      api<{ data: MetaConfig }>('/integrations/meta/config', { method: 'PUT', body }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useTestMetaConfig() {
  return useMutation({
    mutationFn: () =>
      api<{ data: { ok: boolean; app_id: string; app_name: string } }>(
        '/integrations/meta/config/test',
        { method: 'POST' },
      ).then((r) => r.data),
  });
}

/**
 * Fetched only on demand — opening the settings screen should not put the
 * verify token into a response that then sits in the query cache.
 */
export function useRevealVerifyToken() {
  return useMutation({
    mutationFn: () =>
      api<{ data: { verify_token: string | null } }>('/integrations/meta/config/verify-token').then(
        (r) => r.data.verify_token,
      ),
  });
}

export function useMetaAccount() {
  return useQuery({
    queryKey: ['meta', 'account'],
    queryFn: () => api<{ data: MetaAccount }>('/integrations/meta/account').then((r) => r.data),
  });
}

export function useStartMetaOAuth() {
  return useMutation({
    mutationFn: (redirect_uri: string) =>
      api<{ data: { url: string; state: string; expires_in: number } }>(
        '/integrations/meta/account/oauth/start',
        { method: 'POST', body: { redirect_uri } },
      ).then((r) => r.data),
  });
}

export function useCompleteMetaOAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; state: string }) =>
      api<{ data: MetaAccount }>('/integrations/meta/account/oauth/callback', {
        method: 'POST',
        body,
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

/** The pre-App-Review path: a token pasted from the Graph API Explorer. */
export function useConnectMetaToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (user_access_token: string) =>
      api<{ data: MetaAccount }>('/integrations/meta/account/token', {
        method: 'POST',
        body: { user_access_token },
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useDisconnectMetaAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<unknown>('/integrations/meta/account', { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useMetaPages() {
  return useQuery({
    queryKey: ['meta', 'pages'],
    queryFn: () => api<MetaPagesResponse>('/integrations/meta/pages'),
  });
}

export function useLinkMetaPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (page_id: string) =>
      api<{ data: { id: string; page_id: string; name: string; subscribed: boolean } }>(
        '/integrations/meta/pages',
        { method: 'POST', body: { page_id } },
      ).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useResyncMetaPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ data: { id: string; subscribed: boolean; subscribed_fields: string[] } }>(
        `/integrations/meta/pages/${id}/resync`,
        { method: 'POST' },
      ).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useUnlinkMetaPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ data: { unlinked: boolean; unsubscribed: boolean; forms_deactivated: number } }>(
        `/integrations/meta/pages/${id}`,
        { method: 'DELETE' },
      ).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useSyncMetaForms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) =>
      api<{ data: { created: number; updated: number; total: number } }>(
        `/integrations/meta/pages/${pageId}/forms/sync`,
        { method: 'POST' },
      ).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useMetaForms(pageId?: string) {
  return useQuery({
    queryKey: ['meta', 'forms', pageId ?? 'all'],
    queryFn: () =>
      api<{ data: MetaLeadForm[] }>(
        `/integrations/meta/forms?include_inactive=true${pageId ? `&page_id=${pageId}` : ''}`,
      ).then((r) => r.data),
  });
}

/** The single-form read, which is the only one carrying `suggested_mapping`. */
export function useMetaForm(formId: string | undefined) {
  return useQuery({
    queryKey: ['meta', 'form', formId],
    queryFn: () => api<{ data: MetaLeadForm }>(`/integrations/meta/forms/${formId}`).then((r) => r.data),
    enabled: Boolean(formId),
  });
}

export function useUpdateMetaForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      field_mapping?: MetaFieldMapping;
      default_source_id?: string | null;
      is_active?: boolean;
    }) =>
      api<{ data: MetaLeadForm }>(`/integrations/meta/forms/${id}`, { method: 'PATCH', body }).then(
        (r) => r.data,
      ),
    onSuccess: (data) => {
      qc.setQueryData(['meta', 'form', data.id], data);
      void qc.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

export function useMetaAdAccounts() {
  return useQuery({
    queryKey: ['meta', 'ad-accounts'],
    queryFn: () => api<MetaAdAccountsResponse>('/integrations/meta/ad-accounts'),
  });
}

export function useLinkMetaAdAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (external_id: string) =>
      api<{ data: MetaAdAccount }>('/integrations/meta/ad-accounts', {
        method: 'POST',
        body: { external_id },
      }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useUnlinkMetaAdAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<unknown>(`/integrations/meta/ad-accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

export function useSyncMetaCampaigns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ad_account_id?: string) =>
      api<{ data: { ad_accounts: number; campaigns: number; ad_sets: number; ads: number } }>(
        '/integrations/meta/campaigns/sync',
        { method: 'POST', body: ad_account_id ? { ad_account_id } : {} },
      ).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta'] }),
  });
}

/**
 * Readable by any authenticated user, unlike the rest of this module — the lead
 * list filters by campaign and counsellors need the names.
 */
export function useMetaCampaigns(filters: { search?: string; status?: string } = {}) {
  const params = new URLSearchParams({ limit: '200' });
  if (filters.search?.trim()) params.set('search', filters.search.trim());
  if (filters.status) params.set('status', filters.status);

  return useQuery({
    queryKey: ['meta', 'campaigns', filters.search ?? '', filters.status ?? ''],
    queryFn: () =>
      api<{ data: MetaCampaign[] }>(`/integrations/meta/campaigns?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
}

/* -------------------------------- API keys -------------------------------- */

/** The signed-in user's own key. `null` when they have not issued one. */
export function useMyApiKey() {
  return useQuery({
    queryKey: ['api-keys', 'me'],
    queryFn: () => api<{ data: ApiKey | null }>('/api-keys/me').then((r) => r.data),
  });
}

/** Every live key in the organisation. Needs api_key.manage. */
export function useApiKeys(enabled = true) {
  return useQuery({
    queryKey: ['api-keys', 'all'],
    queryFn: () => api<{ data: ApiKey[] }>('/api-keys').then((r) => r.data),
    enabled,
  });
}

/**
 * Issues a key, or rotates the existing one — the server treats both as the
 * same operation, since there is one live key per person.
 *
 * The response carries the secret, which exists nowhere else and cannot be
 * fetched again, so the caller must show it immediately.
 */
export function useIssueApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; permissions?: string[] }) =>
      api<{ data: IssuedApiKey; notice: string }>('/api-keys', { method: 'POST', body }).then(
        (r) => r.data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<unknown>(`/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

/* ---------------------------- lead statuses ---------------------------- */

/**
 * The live status vocabulary. Active only unless `includeInactive`, which the
 * server honours for status managers alone.
 */
export function useLeadStatuses(includeInactive = false) {
  return useQuery({
    queryKey: ['lead-statuses', includeInactive],
    queryFn: () =>
      api<{ data: LeadStatusDef[] }>(`/lead-statuses${includeInactive ? '?include_inactive=true' : ''}`).then(
        (r) => r.data,
      ),
    staleTime: 5 * 60_000,
  });
}

/** Everything that shows a status or a count by status. */
function invalidateStatusViews(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['lead-statuses', 'lead-sub-statuses', 'leads', 'lead', 'dashboard', 'performance']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useCreateLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; category: string; color: string }) =>
      api<LeadStatusDef>('/lead-statuses', { method: 'POST', body }),
    onSuccess: () => invalidateStatusViews(qc),
  });
}

export function useDeleteLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, move_to }: { key: string; move_to?: string }) =>
      api<{ moved_leads: number }>(`/lead-statuses/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        body: move_to ? { move_to } : {},
      }),
    onSuccess: () => invalidateStatusViews(qc),
  });
}

export function useRestoreLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      api<void>(`/lead-statuses/${encodeURIComponent(key)}/restore`, { method: 'POST' }),
    onSuccess: () => invalidateStatusViews(qc),
  });
}

/* ------------------------------- campaigns ------------------------------- */

/** Active campaigns for pickers; `includeInactive` is honoured for managers only. */
export function useCampaigns(includeInactive = false) {
  return useQuery({
    queryKey: ['campaigns', includeInactive],
    queryFn: () =>
      api<{ data: Campaign[] }>(`/campaigns${includeInactive ? '?include_inactive=true' : ''}`).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

function invalidateCampaignViews(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['campaigns', 'lead-campaigns', 'leads', 'lead']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) => api<Campaign>('/campaigns', { method: 'POST', body }),
    onSuccess: () => invalidateCampaignViews(qc),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; is_active?: boolean }) =>
      api<void>(`/campaigns/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateCampaignViews(qc),
  });
}

export function useSetLeadCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, campaign_id }: { id: string; campaign_id: string | null }) =>
      api<Lead>(`/leads/${id}/campaign`, { method: 'POST', body: { campaign_id } }),
    onSuccess: (lead, vars) => {
      qc.setQueryData(['lead', vars.id], lead);
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead-campaigns'] });
    },
  });
}

/* ----------------------------- login activity ----------------------------- */

export interface LoginActivityQuery {
  search?: string;
  role?: string[];
  team_id?: string;
  user_id?: string;
  state?: string[];
  from?: string;
  to?: string;
  limit: number;
  offset: number;
}

export function useLoginActivity(query: LoginActivityQuery) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    p.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  return useQuery({
    queryKey: ['login-activity', p.toString()],
    queryFn: () => api<LoginActivityPage>(`/login-activity?${p}`),
    placeholderData: (prev) => prev,
    // "Active now" drifts as people work; keep the screen honest.
    refetchInterval: 60_000,
  });
}

/* --------------------------- performance reports --------------------------- */

export function useSendReports() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_ids: string[]; from: string; to: string; note?: string; test_email?: string }) =>
      api<{ batch_id: string; data: ReportSend[] }>('/reports/performance/send', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['report-sends'] }),
  });
}

/** One batch while it is being sent (polls until nothing is pending), or the recent log. */
export function useReportSends(batchId: string | null, recent = false) {
  return useQuery({
    queryKey: batchId ? ['report-sends', 'batch', batchId] : ['report-sends', 'recent'],
    queryFn: () =>
      api<{ data: ReportSend[] }>(
        `/reports/performance/sends?${batchId ? `batch_id=${batchId}` : 'limit=25'}`,
      ).then((r) => r.data),
    enabled: Boolean(batchId) || recent,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.status === 'queued' || r.status === 'sending') ? 2000 : false,
  });
}

/* -------------------------------- WhatsApp -------------------------------- */

export function useWhatsAppStatus() {
  return useQuery({
    queryKey: ['whatsapp', 'status'],
    queryFn: () => api<{ data: WhatsAppStatus }>('/integrations/whatsapp/status').then((r) => r.data),
    // The recent-messages table is how an admin watches delivery receipts arrive.
    refetchInterval: 20_000,
  });
}

export function useWhatsAppTemplates(enabled: boolean) {
  return useQuery({
    queryKey: ['whatsapp', 'templates'],
    queryFn: () => api<{ data: WhatsAppTemplate[] }>('/integrations/whatsapp/templates').then((r) => r.data),
    enabled,
    staleTime: 60_000,
  });
}

export function useConnectWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { waba_id: string; phone_number_id: string; access_token: string }) =>
      api<{ data: { subscribed: boolean; subscribe_error: string | null } }>('/integrations/whatsapp/connection', {
        method: 'PUT',
        body,
      }).then((r) => r.data),
    onSettled: () => qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  });
}

export function useDisconnectWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/integrations/whatsapp/connection', { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  });
}

export function useSaveWhatsAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WhatsAppSettingsInput) =>
      api<{ data: WhatsAppStatus }>('/integrations/whatsapp/settings', { method: 'PUT', body }).then((r) => r.data),
    onSuccess: (data) => qc.setQueryData(['whatsapp', 'status'], data),
  });
}

export function useSendWhatsAppTest() {
  return useMutation({
    mutationFn: (body: { to: string; template_name: string; template_language: string; variables: WhatsAppVariable[] }) =>
      api<{ data: { wamid: string | null; preview: string } }>('/integrations/whatsapp/test', {
        method: 'POST',
        body,
      }).then((r) => r.data),
  });
}
